import { useEffect, useState } from "react";

import {
  currentBlock,
  currentMenuBlock,
  FOCUS_BLOCK_MENU_EVENT,
  isFocusInBlockControls,
} from "@/components/tiptap/block-navigation/BlockNavigation";

import { menuBlockAt } from "./lineMenuBlocks";

import type { Editor } from "@tiptap/core";

/**
 * The block whose menu button is a read-only editor's Tab stop: the one the keyboard last moved
 * to, else the one holding the selection, else the nearest block with a menu.
 */
function tabStopBlockOf(editor: Editor): number | null {
  return editor.isEditable ? null : currentMenuBlock(editor);
}

/**
 * The block whose controls have keyboard focus, or are about to get it. A collapsed block has its
 * menu too, which can expand it.
 */
function navigationBlockOf(editor: Editor, isFocusRequested: boolean): number | null {
  if (!isFocusRequested && !isFocusInBlockControls(editor.view)) {
    return null;
  }

  const pos = currentBlock(editor.state);
  return pos === null ? null : menuBlockAt(editor, pos, { withCollapsed: true });
}

/**
 * The block whose controls have keyboard focus, whose menu takes precedence over the hovered
 * one, and the requests to focus its menu button (`FOCUS_BLOCK_MENU_EVENT`).
 */
export function useNavigationMenuBlock(editor: Editor | undefined, root: HTMLElement | null) {
  const [pos, setPos] = useState<number | null>(null);
  const [tabStopPos, setTabStopPos] = useState<number | null>(null);
  const [isFocusRequested, setIsFocusRequested] = useState(false);
  /** The menu was reached from the keyboard rather than with the pointer. */
  const [isKeyboardMenu, setIsKeyboardMenu] = useState(false);

  useEffect(() => {
    if (!editor || !root) {
      return;
    }

    const sync = () => {
      setPos(navigationBlockOf(editor, isFocusRequested));
      setTabStopPos(tabStopBlockOf(editor));
    };

    const onFocusRequest = () => {
      setIsKeyboardMenu(true);
      setIsFocusRequested(true);
      setPos(navigationBlockOf(editor, true));
    };

    sync();
    editor.on("transaction", sync);
    // Switching between editable and read-only moves the Tab stop.
    editor.on("update", sync);
    root.addEventListener("focusin", sync);
    root.addEventListener("focusout", sync);
    root.addEventListener(FOCUS_BLOCK_MENU_EVENT, onFocusRequest);

    return () => {
      editor.off("transaction", sync);
      editor.off("update", sync);
      root.removeEventListener("focusin", sync);
      root.removeEventListener("focusout", sync);
      root.removeEventListener(FOCUS_BLOCK_MENU_EVENT, onFocusRequest);
    };
  }, [editor, root, isFocusRequested]);

  return {
    pos,
    /** In a read-only editor, the block whose menu button is the Tab stop when nothing else is. */
    tabStopPos,
    isFocusRequested,
    isKeyboardMenu,
    /** The menu button took the requested focus. */
    focusDone: () => setIsFocusRequested(false),
    /** The pointer is on the menu, so it is no longer a keyboard user's. */
    pointerUsed: () => setIsKeyboardMenu(false),
  };
}
